import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Badge, Spinner } from 'flowbite-react'
import { HiArrowLeft, HiOfficeBuilding } from 'react-icons/hi'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import { API_BASE } from '../constants/inviteCode'
import { toastApiError } from '../utils/apiError'
import AuthorityAutocomplete from '../components/AuthorityAutocomplete'
import DepartmentList from '../components/DepartmentList'
import DefaultTemplatesPanel from '../components/DefaultTemplatesPanel'

/**
 * Departments & areas-of-responsibility management. Any authenticated user can
 * read; a user may write only to their own authority (me.authority_id), while a
 * superuser may pick and manage any authority via ?authority=<id>.
 */
const DepartmentAdmin = () => {
  const { token, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const paramAuthorityId = searchParams.get('authority')
  const ownAuthorityId = user?.authority_id != null ? String(user.authority_id) : null
  const authorityId = paramAuthorityId || ownAuthorityId

  const isSuperuser = Boolean(user?.is_superuser)
  const canWrite =
    isSuperuser ||
    (ownAuthorityId != null && authorityId != null && ownAuthorityId === String(authorityId))

  const [authority, setAuthority] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!authorityId) {
      setAuthority(null)
      return
    }
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const response = await axios.get(`${API_BASE}/authorities/${authorityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!cancelled) setAuthority(response.data)
      } catch (error) {
        if (!cancelled) {
          toastApiError(error, 'Failed to load authority')
          setAuthority(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [authorityId, token])

  // No authority resolved: superusers pick one; others without an authority get a notice.
  if (!authorityId) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            Department Administration
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage each authority&apos;s departments and areas of responsibility.
          </p>
        </div>
        {isSuperuser ? (
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Choose an authority to manage its departments.
            </p>
            <AuthorityAutocomplete
              token={token}
              autoFocus
              onSelect={(item) => setSearchParams({ authority: String(item.id) })}
            />
          </div>
        ) : (
          <div className="rounded-lg bg-white p-12 text-center shadow dark:bg-gray-800">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <HiOfficeBuilding className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
              No authority linked to your account
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your account isn&apos;t associated with an authority yet, so there are no
              departments to manage. Contact an administrator if this looks wrong.
            </p>
          </div>
        )}
      </div>
    )
  }

  const showBackToPicker = isSuperuser && Boolean(paramAuthorityId)

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        {showBackToPicker && (
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="mb-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <HiArrowLeft className="mr-1 h-4 w-4" />
            Choose a different authority
          </button>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {loading && !authority ? 'Departments' : authority?.name || 'Departments'}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              Departments and areas of responsibility route incoming citizen reports.
            </p>
          </div>
          {!canWrite && (
            <Badge color="gray">Read only — you can&apos;t manage this authority</Badge>
          )}
        </div>
        {isSuperuser && authority?.official_domain && (
          <Link
            to={`/city-admin/${authorityId}`}
            className="mt-3 inline-block text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Open city profile →
          </Link>
        )}
      </div>

      <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        {loading && !authority ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" />
          </div>
        ) : (
          <DepartmentList token={token} authorityId={authorityId} canWrite={canWrite} />
        )}
      </div>

      <DefaultTemplatesPanel
        token={token}
        defaultTypeId={authority?.authority_type_id ?? null}
      />
    </div>
  )
}

export default DepartmentAdmin
